import React from "react";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import Tooltip from "@edaz/edazkit/Tooltip/index";
import Button from "@edaz/edazkit/Button/index";
import type { IApprovable } from "entities/types";

export interface ApproveToolboxProps<TEntity extends IApprovable> {
  approval: TEntity;
  myApprovalStep: (approvable: TEntity) => boolean;
  approvedByMe: (approvable: TEntity) => boolean;
  isExported: (exportable: TEntity) => boolean;
  canApprove: (approvable: TEntity) => boolean | undefined;
  approve: (approvable: TEntity) => () => Promise<void>;
  translate: (code: string) => string;
  disabled?: boolean;
  buttonsStyle?: string;
  rejectionReason?: string;
}

function ApproveToolbox<TEntity extends IApprovable>({
  approval,
  myApprovalStep,
  approvedByMe,
  isExported,
  canApprove,
  approve,
  translate,
  buttonsStyle = undefined,
  disabled = false,
  rejectionReason,
}: ApproveToolboxProps<TEntity>) {
  const showApprovedLabel = approvedByMe(approval) && !isExported(approval);
  const showExportedLabel = isExported(approval);

  // We need to fake disabled state to render tooltip on hover, because react-bootstrap doesn't propagate
  // events on disabled buttons
  const ApproveButton = ({ disabled, rejectionReason }: { disabled?: boolean; rejectionReason?: string }) => {
    // Button is disabled and we have rejection reason
    if (disabled && rejectionReason) {
      return (
        <Tooltip
          tooltip={<div style={{ padding: "5px 0" }}>{translate(`rejection.${rejectionReason}`)}</div>}
          trigger={["hover", "focus"]}
          id={`rejection-detail-${approval.id}`}>
          <div className="kit__button">
            <Button size="sm" className={classNames(buttonsStyle, { disabled })} onClick={() => null}>
              {translate("approve")}
            </Button>
          </div>
        </Tooltip>
      );
    }

    // Otherwise return button without tooltip (disabled or not)
    return (
      <Button size="sm" className={buttonsStyle} disabled={disabled} onClick={approve(approval)}>
        {translate("approve")}
      </Button>
    );
  };

  const buttonDisabled = !canApprove(approval) || disabled;

  return (
    <>
      {myApprovalStep(approval) && !approvedByMe(approval) && (
        <div className="align-self-center justify-content-center">
          <ApproveButton disabled={buttonDisabled} rejectionReason={rejectionReason} />
        </div>
      )}
      {showApprovedLabel && <div className="text-success font-weight-bold p-2 px-3">{translate("approved")}</div>}
      {showExportedLabel && <div className="text-info font-weight-bold p-2 px-3">{translate("exported")}</div>}
    </>
  );
}

export default observer(ApproveToolbox);
